<script setup lang="ts">
import { ref, watch } from 'vue';

// PROPS
const props = withDefaults(
  defineProps<{
    tabs: { id: string; name: string; isDirty?: boolean; icon?: string; disable?: boolean }[];
    active: string;
    keepAlive?: boolean | undefined;
    vertical?: boolean | undefined;
    showSplitter?: boolean | undefined;
  }>(),
  { showSplitter: true, vertical: true },
);

// REFS
const model = ref(props.active);
const splitterModel = ref(330);
const isManuallyChanged = ref(false);

// EMITS
const emit = defineEmits<{
  (e: 'update', tabId: string): void;
}>();

// METHODS
const setActiveTab = (tabId: string) => {
  isManuallyChanged.value = true;
  model.value = tabId;
};

// Expose the method to parent
defineExpose({
  setActiveTab,
});

// WATCH
watch(
  () => model.value,
  (newValue) => {
    if (!isManuallyChanged.value) emit('update', newValue);
    isManuallyChanged.value = false;
  },
);
</script>
<template>
  <div class="dynamic-tabs">
    <q-splitter v-if="props.showSplitter" v-model="splitterModel" unit="px" :limits="[300, 360]">
      <template v-slot:before>
        <q-tabs
          v-model="model"
          :vertical="props.vertical"
          class="q-grey"
          active-color="black"
          :active-class="active"
          indicator-color="black"
          align="left"
        >
          <q-tab
            v-for="tab in props.tabs"
            :key="tab.id"
            :name="tab.id"
            :label="tab.name"
            :disable="tab.disable"
            :alert="tab.isDirty ? `red` : false"
            :icon="tab.icon"
          />
        </q-tabs>
      </template>
      <template v-slot:after>
        <q-tab-panels
          v-model="model"
          animated
          transition-prev="jump-left"
          transition-next="jump-right"
          :keep-alive="props.keepAlive"
        >
          <q-tab-panel v-for="tab in tabs" :key="tab.id" :name="tab.id">
            <slot :name="tab.id"></slot>
          </q-tab-panel>
        </q-tab-panels>
      </template>
    </q-splitter>
    <div v-else class="no-splitter-layout">
      <q-tabs
        v-model="model"
        :vertical="props.vertical"
        class="q-grey"
        :class="{ 'q-tabs--horizontal': !props.vertical }"
        active-color="black"
        :active-class="active"
        indicator-color="black"
        align="left"
      >
        <q-tab
          v-for="tab in props.tabs"
          :key="tab.id"
          :name="tab.id"
          :label="tab.name"
          :disable="tab.disable"
          :alert="tab.isDirty ? `red` : false"
          :icon="tab.icon"
        />
      </q-tabs>
    </div>
  </div>
</template>
<style lang="scss">
.dynamic-tabs {
  .q-splitter {
    padding: 20px;
  }

  .q-tab {
    justify-content: flex-start;
  }

  .q-tab__content {
    display: flex;
    flex-direction: row;
    gap: 10px;
    justify-content: flex-start;
  }

  .q-tab--active {
    background-color: $color-neutral-grey-200;
    display: flex;

    .q-tab__label {
      font-weight: bold;
    }
  }

  .q-tab {
    min-height: 36px;
  }

  .q-tab--full {
    min-height: 52px;
  }

  .q-tab-panel {
    overflow-x: hidden;
    padding-top: 0;
  }

  .q-tabs--horizontal .q-tab__indicator {
    height: 3px !important;
  }
}
</style>
